import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { Box, Flex, Heading, Icon, Text } from '@qga/roo-ui/components';
import { themeGet } from 'styled-system';

import type { PackageOption } from '@shared/v2/utils/getPackageOptions';
import useFlyingFrom from '@shared/v2/hooks/useFlyingFrom';

import Button from '@/components/Button';
import useProcuredOffer from '@/components/PackagePage/hooks/useProcuredOffer';
import usePropertyContent from '@/components/PackagePage/hooks/usePropertyContent';
import { ORIGINS } from '@/constants';
import { SCREEN_SIZE } from '@/lib/theme';
import PackageInclusions from '@/v2/components/ui/PackageInclusions';
import { UI } from '@/v2/types';
import { getOriginName } from '@/v2/utils/getOriginName';
import { OtherPackageOffer } from '../../../../contexts/OtherPackageModalContext/utils';
import OfferPrice from '../../../RoomOptions/components/RoomOption/components/CTA/components/OfferPrice';
import LeadInOffer from '../LeadInOffer';
import UnavailableMessage from '../UnavailableMessage';

export interface PackageOptionProps {
  offer: OtherPackageOffer;
  isPreSearch?: boolean;
  route?: string;
  isNotSelectable?: boolean;
  isAlternativeModal?: boolean;
  onSelectPackageOption?: (args: {
    packageOption?: PackageOption;
    roomId?: string;
  }) => void;
}

const Main = styled(Flex)`
  max-width: 405px;
`;

const Aside = styled(Flex)`
  max-width: 100%;
  ${SCREEN_SIZE.SM} {
    max-width: 248px;
  }
`;

const StyledButton = styled(Button)`
  padding: 6px;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 1.5px;
`;

const StyledBox = styled(Box)`
  border-width: ${themeGet('components.packageOption.borderWidth')};
  border-radius: ${themeGet('components.packageOption.borderRadius')};
  border-style: solid;
  border-color: ${themeGet(`components.packageOption.default.borderColor`)};
  overflow: hidden;
  background: ${themeGet(`background.card`)};
  width: 100%;
`;

const PackageOptionCard = ({
  offer: packageOffer,
  route,
  isPreSearch,
  isNotSelectable,
  onSelectPackageOption,
  isAlternativeModal,
}: PackageOptionProps) => {
  const propertyContent: UI.Property = usePropertyContent();
  const procuredOffer: UI.ProcuredOffer = useProcuredOffer();
  const { packageOption, leadInOffer, offer } = packageOffer;

  const handleSelectPackageOption = () => {
    onSelectPackageOption?.({
      packageOption,
      roomId: offer?.roomType?.id,
    });
  };

  const baseInclusions = useMemo(() => {
    const leadInOfferInclusions = isPreSearch
      ? leadInOffer?.inclusions
      : offer?.leadInOffer?.inclusions;

    return leadInOfferInclusions || offer?.hotelOffer?.inclusions || [];
  }, [offer, isPreSearch, leadInOffer?.inclusions]);

  let inclusions = baseInclusions || [];
  if (isNotSelectable) {
    inclusions = packageOption?.inclusions ?? [];
  }

  const originCodes = useMemo(() => ORIGINS.map((o) => o.code), []);
  const { originCode } = useFlyingFrom(originCodes);
  const originName = useMemo(() => {
    if (route && originCode && route.startsWith(originCode)) {
      return getOriginName(originCode);
    }
  }, [originCode, route]);

  const textColor = isNotSelectable ? 'greys.steel' : 'greys.charcoal';

  const dividerProps =
    isNotSelectable || isPreSearch
      ? {
          borderLeft: '3px solid',
          borderColor: isNotSelectable ? 'orange' : 'brand.primary',
          pl: 5,
        }
      : {};

  return (
    <StyledBox px={6} pb={6} pt={4}>
      <Heading.h6
        lineHeight={['20px', null, '22px']}
        fontSize={['16px', null, '18px']}
        mb={6}
        color={textColor}
      >
        {packageOption?.title || 'Best available rate'}
      </Heading.h6>
      <Flex
        gap={'16px'}
        flexDirection={['column', null, 'row']}
        justifyContent={[null, null, 'space-between']}
      >
        <Main flexDirection="column" gap={'8px'}>
          {offer?.roomType && (
            <Flex flexDirection="column" gap="8px" mb="8px">
              <Text
                fontSize={['14px', null, '16px']}
                lineHeight={['20px', null, '24px']}
                letterSpacing="0.1px"
              >
                Room type:
              </Text>
              <Flex>
                <Icon
                  name="hotel"
                  size={[22, null, 24]}
                  color="icon.inclusion"
                />
                <Text
                  ml="2"
                  color="text.body"
                  fontSize={['xs', null, 'sm']}
                  lineHeight={['20px', null, '24px']}
                  letterSpacing="0.1px"
                >
                  {offer?.roomType.name}
                </Text>
              </Flex>
            </Flex>
          )}
          {inclusions?.length > 0 && (
            <PackageInclusions
              procuredOffer={procuredOffer}
              inclusions={inclusions}
              propertyContent={propertyContent}
              color={textColor}
            />
          )}
        </Main>
        <Aside
          gap="16px"
          flexDirection="column"
          flex="1"
          justifyContent={isNotSelectable ? 'center' : 'start'}
        >
          <Box {...dividerProps}>
            {isNotSelectable ? (
              <UnavailableMessage isAlternativeModal={isAlternativeModal} />
            ) : (
              <Box>
                {isPreSearch && leadInOffer && (
                  <LeadInOffer
                    originName={originName}
                    route={route}
                    offer={leadInOffer}
                  />
                )}
                {!isPreSearch && offer && (
                  <OfferPrice offer={offer} includeDivider={false} />
                )}
              </Box>
            )}
          </Box>
          {!isNotSelectable && (
            <StyledButton
              type="button"
              variant="primary"
              width="100%"
              mt="2"
              onClick={handleSelectPackageOption}
            >
              Select and continue
            </StyledButton>
          )}
        </Aside>
      </Flex>
    </StyledBox>
  );
};

export default PackageOptionCard;
